/**
 * Scene-level sketch utilities — math helpers, eraser hit-test,
 * world-position computation, and `pruneSceneSketch`.
 *
 * Replaces `surfaceStrokes.ts`. Strokes live at `AppState.sketch`, not on
 * any individual SceneObject; the helpers here read the new
 * `SketchStroke` discriminated union.
 */

import * as THREE from "three/webgpu";
import type {
  SketchLayer,
  SketchStroke,
  ProjectedStroke,
  ProjectedStrokePoint,
  SceneSketchState,
  ScreenspaceStroke,
} from "../modeling/sketch.ts";
import { cloneSceneSketchState, isProjectedStroke } from "../modeling/sketch.ts";
import type { SceneObject } from "../modeling/types.ts";

// ---------------------------------------------------------------------------
// Hit → bind-pose conversion (unchanged math; lifted from surfaceStrokes.ts)
// ---------------------------------------------------------------------------

const _hit_e0 = new THREE.Vector3();
const _hit_e1 = new THREE.Vector3();
const _hit_e2 = new THREE.Vector3();
const _hit_pA = new THREE.Vector3();
const _hit_pB = new THREE.Vector3();
const _hit_pC = new THREE.Vector3();
const _hit_local = new THREE.Vector3();

function finiteBary(bary: THREE.Vector3 | null | undefined): boolean {
  return (
    !!bary &&
    Number.isFinite(bary.x) &&
    Number.isFinite(bary.y) &&
    Number.isFinite(bary.z)
  );
}

function readRaycastVertexPosition(
  mesh: THREE.Mesh,
  posAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  vertexIndex: number,
  out: THREE.Vector3,
): void {
  const skinnedMesh =
    (mesh as THREE.SkinnedMesh).isSkinnedMesh &&
    (mesh as THREE.SkinnedMesh).skeleton
      ? (mesh as THREE.SkinnedMesh)
      : null;
  if (skinnedMesh) {
    skinnedMesh.getVertexPosition(vertexIndex, out);
    return;
  }
  out.fromBufferAttribute(posAttr, vertexIndex);
}

function resolveObjectVertexIndex(
  geometryVertexIndex: number,
  geometryVertexCount: number,
  obj: SceneObject,
): number {
  const objectVertexCount = obj.vertices.length / 3;
  if (geometryVertexCount === objectVertexCount) {
    return geometryVertexIndex < objectVertexCount ? geometryVertexIndex : -1;
  }
  if (geometryVertexIndex < obj.topology.corners.length) {
    return obj.topology.corners[geometryVertexIndex];
  }
  return geometryVertexIndex < objectVertexCount ? geometryVertexIndex : -1;
}

export interface BindPoseHit {
  /** Bind-pose mesh-local position. */
  localPos: [number, number, number];
  /** Barycentric coords (u,v,w) in the hit triangle. */
  bary: [number, number, number];
  /** Geometry vertex indices (face.a/b/c) used by the skin bake to look up
   *  skinIndex/skinWeight buffer entries. */
  corners: [number, number, number];
}

/**
 * Map a proxy-mesh raycast hit back to bind-pose space via barycentric
 * interpolation of the hit triangle's three shared-vertex corners.
 */
export function hitToBindPose(
  hit: THREE.Intersection,
  mesh: THREE.Mesh,
  obj: SceneObject,
): BindPoseHit | null {
  const face = hit.face;
  if (!face) return null;
  if (!obj.vertices) return null;

  const posAttr = mesh.geometry.getAttribute("position");
  if (!posAttr) return null;
  const geometryVertexCount = posAttr.count;

  if (
    face.a >= geometryVertexCount ||
    face.b >= geometryVertexCount ||
    face.c >= geometryVertexCount
  ) {
    return null;
  }

  const vA = resolveObjectVertexIndex(face.a, geometryVertexCount, obj);
  const vB = resolveObjectVertexIndex(face.b, geometryVertexCount, obj);
  const vC = resolveObjectVertexIndex(face.c, geometryVertexCount, obj);
  if (vA < 0 || vB < 0 || vC < 0) return null;

  let bu: number;
  let bv: number;
  let bw: number;
  if (finiteBary(hit.barycoord)) {
    bu = hit.barycoord!.x;
    bv = hit.barycoord!.y;
    bw = hit.barycoord!.z;
  } else {
    readRaycastVertexPosition(mesh, posAttr, face.a, _hit_pA);
    readRaycastVertexPosition(mesh, posAttr, face.b, _hit_pB);
    readRaycastVertexPosition(mesh, posAttr, face.c, _hit_pC);

    _hit_local.copy(hit.point);
    mesh.worldToLocal(_hit_local);

    _hit_e0.subVectors(_hit_pB, _hit_pA);
    _hit_e1.subVectors(_hit_pC, _hit_pA);
    _hit_e2.subVectors(_hit_local, _hit_pA);
    const d00 = _hit_e0.dot(_hit_e0);
    const d01 = _hit_e0.dot(_hit_e1);
    const d11 = _hit_e1.dot(_hit_e1);
    const d20 = _hit_e2.dot(_hit_e0);
    const d21 = _hit_e2.dot(_hit_e1);
    const denom = d00 * d11 - d01 * d01;
    if (Math.abs(denom) < 1e-12) return null;
    const inv = 1 / denom;
    bv = (d11 * d20 - d01 * d21) * inv;
    bw = (d00 * d21 - d01 * d20) * inv;
    bu = 1 - bv - bw;
  }

  if (!Number.isFinite(bu) || !Number.isFinite(bv) || !Number.isFinite(bw)) {
    return null;
  }

  const v = obj.vertices;
  const a3 = vA * 3;
  const b3 = vB * 3;
  const c3 = vC * 3;
  return {
    localPos: [
      v[a3] * bu + v[b3] * bv + v[c3] * bw,
      v[a3 + 1] * bu + v[b3 + 1] * bv + v[c3 + 1] * bw,
      v[a3 + 2] * bu + v[b3 + 2] * bv + v[c3 + 2] * bw,
    ],
    bary: [bu, bv, bw],
    corners: [face.a, face.b, face.c],
  };
}

// ---------------------------------------------------------------------------
// Skin-weight baking
// ---------------------------------------------------------------------------

/**
 * Barycentric-merge the hit triangle's three corner skin attributes into a
 * single top-4 (index, weight) set for one stroke point.
 */
export function bakeSkinAttachment(
  mesh: THREE.Mesh,
  corners: [number, number, number],
  bary: [number, number, number],
): {
  skinIndex: [number, number, number, number];
  skinWeight: [number, number, number, number];
} | null {
  const skinIdx = mesh.geometry.getAttribute("skinIndex");
  const skinWt = mesh.geometry.getAttribute("skinWeight");
  if (!skinIdx || !skinWt) return null;

  const merged = new Map<number, number>();
  for (let k = 0; k < 3; k++) {
    const corner = corners[k];
    const bw = bary[k];
    if (bw <= 0) continue;
    for (let s = 0; s < 4; s++) {
      const boneIdx = skinIdx.getComponent(corner, s);
      const w = skinWt.getComponent(corner, s) * bw;
      if (w <= 0) continue;
      merged.set(boneIdx, (merged.get(boneIdx) ?? 0) + w);
    }
  }

  if (merged.size === 0) return null;

  const sorted = Array.from(merged.entries()).sort((a, b) => b[1] - a[1]);
  const top: [number, number][] = [];
  for (let i = 0; i < 4; i++) top.push(sorted[i] ?? [0, 0]);

  const total = top[0][1] + top[1][1] + top[2][1] + top[3][1];
  if (total <= 0) return null;

  return {
    skinIndex: [top[0][0], top[1][0], top[2][0], top[3][0]],
    skinWeight: [
      top[0][1] / total,
      top[1][1] / total,
      top[2][1] / total,
      top[3][1] / total,
    ],
  };
}

// ---------------------------------------------------------------------------
// CPU skinning — bind-pose stroke point → world-space position
// ---------------------------------------------------------------------------

const _skin_base = new THREE.Vector3();
const _skin_local = new THREE.Vector3();
const _skin_world = new THREE.Vector3();
const _skin_mat = new THREE.Matrix4();
const _skin_tmp = new THREE.Vector3();
const _skin_triA = new THREE.Vector3();
const _skin_triB = new THREE.Vector3();
const _skin_triC = new THREE.Vector3();

function withStashedMorphInfluences<T>(
  mesh: THREE.SkinnedMesh,
  fn: () => T,
): T {
  const stashed = mesh.userData?.stashedInfluences;
  if (!Array.isArray(stashed) || stashed.length === 0) return fn();
  const previous = mesh.morphTargetInfluences;
  mesh.morphTargetInfluences = stashed as number[];
  try {
    return fn();
  } finally {
    mesh.morphTargetInfluences = previous;
  }
}

/**
 * Compute the current world-space position of one projected stroke point.
 * `mesh.matrixWorld` and skeleton bone world matrices must already be up
 * to date — the caller is responsible for the upstream `update()`.
 */
export function projectedPointWorldPosition(
  point: ProjectedStrokePoint,
  mesh: THREE.Mesh,
  out: THREE.Vector3,
): THREE.Vector3 {
  const skinnedMesh =
    (mesh as THREE.SkinnedMesh).isSkinnedMesh &&
    (mesh as THREE.SkinnedMesh).skeleton
      ? (mesh as THREE.SkinnedMesh)
      : null;
  const wIdx = point.skinIndex;
  const wWt = point.skinWeight;

  if (skinnedMesh && point.corners && point.bary) {
    const posAttr = skinnedMesh.geometry.getAttribute("position");
    const [a, b, c] = point.corners;
    if (
      posAttr &&
      a < posAttr.count &&
      b < posAttr.count &&
      c < posAttr.count
    ) {
      withStashedMorphInfluences(skinnedMesh, () => {
        skinnedMesh.getVertexPosition(a, _skin_triA);
        skinnedMesh.getVertexPosition(b, _skin_triB);
        skinnedMesh.getVertexPosition(c, _skin_triC);
      });
      out
        .set(0, 0, 0)
        .addScaledVector(_skin_triA, point.bary[0])
        .addScaledVector(_skin_triB, point.bary[1])
        .addScaledVector(_skin_triC, point.bary[2])
        .applyMatrix4(skinnedMesh.matrixWorld);
      return out;
    }
  }

  if (!skinnedMesh || !wIdx || !wWt) {
    out.set(point.p[0], point.p[1], point.p[2]).applyMatrix4(mesh.matrixWorld);
    return out;
  }

  _skin_local.set(point.p[0], point.p[1], point.p[2]);
  _skin_base.copy(_skin_local).applyMatrix4(skinnedMesh.bindMatrix);
  out.set(0, 0, 0);
  const bones = skinnedMesh.skeleton.bones;
  const boneInverses = skinnedMesh.skeleton.boneInverses;
  for (let i = 0; i < 4; i++) {
    const w = wWt[i];
    if (w === 0) continue;
    const bi = wIdx[i];
    const bone = bones[bi];
    const inv = boneInverses[bi];
    if (!bone || !inv) continue;
    _skin_mat.multiplyMatrices(bone.matrixWorld, inv);
    out.addScaledVector(_skin_tmp.copy(_skin_base).applyMatrix4(_skin_mat), w);
  }
  out.applyMatrix4(skinnedMesh.bindMatrixInverse);
  _skin_world.copy(out).applyMatrix4(skinnedMesh.matrixWorld);
  out.copy(_skin_world);
  return out;
}

const _swp_tmp = new THREE.Vector3();

export type ProjectedPointMeshResolver = (
  point: ProjectedStrokePoint,
) => THREE.Mesh | null | undefined;

/** Fill `out` with world positions for every point in a projected stroke. */
export function projectedStrokeWorldPositions(
  stroke: ProjectedStroke,
  resolveMesh: ProjectedPointMeshResolver,
  out: Float32Array,
): Float32Array | null {
  for (let i = 0; i < stroke.points.length; i++) {
    const point = stroke.points[i];
    const mesh = resolveMesh(point);
    if (!mesh) return null;
    projectedPointWorldPosition(point, mesh, _swp_tmp);
    out[i * 3] = _swp_tmp.x;
    out[i * 3 + 1] = _swp_tmp.y;
    out[i * 3 + 2] = _swp_tmp.z;
  }
  return out;
}

/**
 * Fill `out` with world positions for a screenspace stroke. Points are
 * already world-space (baked at draw time), so this is just a strided copy.
 */
export function screenspaceStrokeWorldPositions(
  stroke: ScreenspaceStroke,
  out: Float32Array,
): Float32Array {
  for (let i = 0; i < stroke.points.length; i++) {
    const p = stroke.points[i].p;
    out[i * 3] = p[0];
    out[i * 3 + 1] = p[1];
    out[i * 3 + 2] = p[2];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Geometric helpers
// ---------------------------------------------------------------------------

export function distance3(
  a: [number, number, number],
  b: [number, number, number],
): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Intersect a ray with an infinite plane defined by point + normal.
 * Returns the world-space hit point, or null if the ray is parallel to
 * the plane or hits behind the origin.
 */
export function rayPlaneIntersect(
  rayOrigin: THREE.Vector3,
  rayDir: THREE.Vector3,
  planePoint: [number, number, number],
  planeNormal: [number, number, number],
  out: THREE.Vector3,
): THREE.Vector3 | null {
  const nx = planeNormal[0];
  const ny = planeNormal[1];
  const nz = planeNormal[2];
  const denom = rayDir.x * nx + rayDir.y * ny + rayDir.z * nz;
  if (Math.abs(denom) < 1e-9) return null;
  const dx = planePoint[0] - rayOrigin.x;
  const dy = planePoint[1] - rayOrigin.y;
  const dz = planePoint[2] - rayOrigin.z;
  const t = (dx * nx + dy * ny + dz * nz) / denom;
  if (!Number.isFinite(t)) return null;
  out.copy(rayDir).multiplyScalar(t).add(rayOrigin);
  return out;
}

// ---------------------------------------------------------------------------
// Eraser hit-test — screen-space point-to-segment distance
// ---------------------------------------------------------------------------

const _era_tmp = new THREE.Vector3();

function projectToCssPx(
  worldPos: THREE.Vector3,
  camera: THREE.Camera,
  rect: { left: number; top: number; width: number; height: number },
  out: THREE.Vector3,
): boolean {
  out.copy(worldPos).project(camera);
  if (out.z < -1 || out.z > 1) return false;
  out.x = rect.left + ((out.x + 1) / 2) * rect.width;
  out.y = rect.top + ((1 - out.y) / 2) * rect.height;
  return true;
}

function segmentDistSq2D(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

/**
 * Return the ids of strokes (across all visible layers in the given sketch
 * state) whose polyline approaches the cursor within `thresholdPx` on screen.
 *
 * `pointWorldPositions` is keyed by `stroke.id` and supplies live world
 * positions for projected strokes (so the eraser sees the deformed
 * geometry). Screenspace strokes use their own `point.p` directly since
 * they're already world-space.
 *
 * For projected strokes whose host object is missing or whose world
 * positions weren't supplied, the stroke is skipped — there's no safe
 * fallback (the bind-pose `p` is mesh-local, not world).
 */
export function findStrokesUnderCursor(
  sketch: SceneSketchState,
  cursorPx: { x: number; y: number },
  camera: THREE.Camera,
  rect: { left: number; top: number; width: number; height: number },
  thresholdPx: number,
  pointWorldPositions: Map<string, Float32Array>,
): Set<string> {
  const hits = new Set<string>();
  const tmpWorld = new THREE.Vector3();
  const screenA = new THREE.Vector3();
  const screenB = new THREE.Vector3();

  for (const layer of sketch.layers) {
    if (!layer.visible) continue;
    for (const stroke of layer.strokes) {
      const effectiveThreshold = thresholdPx + 0.5 * stroke.thickness;
      const effectiveThresholdSq = effectiveThreshold * effectiveThreshold;
      const pointCount = stroke.points.length;
      if (pointCount === 0) continue;

      const worldPositions = pointWorldPositions.get(stroke.id);

      const pToWorld = (i: number, out: THREE.Vector3) => {
        if (worldPositions) {
          out.set(
            worldPositions[i * 3],
            worldPositions[i * 3 + 1],
            worldPositions[i * 3 + 2],
          );
          return;
        }
        if (isProjectedStroke(stroke)) {
          // No world-position cache for this projected stroke — skip.
          out.set(NaN, NaN, NaN);
          return;
        }
        // Screenspace stroke — point is already world-space.
        const p = stroke.points[i].p;
        _era_tmp.set(p[0], p[1], p[2]);
        out.copy(_era_tmp);
      };

      pToWorld(0, tmpWorld);
      if (!Number.isFinite(tmpWorld.x)) continue;

      if (pointCount === 1) {
        if (!projectToCssPx(tmpWorld, camera, rect, screenA)) continue;
        const dx = cursorPx.x - screenA.x;
        const dy = cursorPx.y - screenA.y;
        if (dx * dx + dy * dy <= effectiveThresholdSq) hits.add(stroke.id);
        continue;
      }

      let prevValid = projectToCssPx(tmpWorld, camera, rect, screenA);

      for (let i = 1; i < pointCount; i++) {
        pToWorld(i, tmpWorld);
        if (!Number.isFinite(tmpWorld.x)) {
          prevValid = false;
          continue;
        }
        const nextValid = projectToCssPx(tmpWorld, camera, rect, screenB);
        if (prevValid && nextValid) {
          const distSq = segmentDistSq2D(
            cursorPx.x,
            cursorPx.y,
            screenA.x,
            screenA.y,
            screenB.x,
            screenB.y,
          );
          if (distSq <= effectiveThresholdSq) {
            hits.add(stroke.id);
            break;
          }
        }
        screenA.copy(screenB);
        prevValid = nextValid;
      }
    }
  }

  return hits;
}

// ---------------------------------------------------------------------------
// pruneSceneSketch — apply object/topology mutations to scene sketch
// ---------------------------------------------------------------------------

/**
 * Returns a new `SceneSketchState` with the requested mutations applied.
 * Always returns a fresh top-level reference; layers/strokes are reused
 * unchanged whenever possible (shallow re-clone only when their contents
 * differ, so undo equality checks still work).
 *
 *  - `removedObjectIds`: drop projected points whose attachment is in this set.
 *  - `bakedTransforms`: for each entry, multiply the matrix into the
 *    bind-pose `point.p` of every projected stroke attached to that object,
 *    leaving skin/corner data untouched. Used by Apply Transform so strokes
 *    stay locked to the surface.
 *  - `topologyBumped`: for each (objectId, newVersion) pair, drop projected
 *    points whose attachment.topologyVersion is older than `newVersion`
 *    AND that carry `corners` data — those corners reference the now-stale
 *    geometry. Points without `corners` (purely skin-bound) survive.
 */
export interface PruneSceneSketchOptions {
  removedObjectIds?: ReadonlySet<string>;
  bakedTransforms?: ReadonlyMap<string, THREE.Matrix4>;
  topologyBumped?: ReadonlyMap<string, number>;
}

export function pruneSceneSketch(
  sketch: SceneSketchState,
  opts: PruneSceneSketchOptions,
): SceneSketchState {
  const removed = opts.removedObjectIds;
  const baked = opts.bakedTransforms;
  const bumped = opts.topologyBumped;

  const _v = new THREE.Vector3();
  let anyChange = false;

  const nextLayers: SketchLayer[] = sketch.layers.map((layer) => {
    let layerChanged = false;
    const nextStrokes: SketchStroke[] = [];
    for (const stroke of layer.strokes) {
      if (!isProjectedStroke(stroke)) {
        nextStrokes.push(stroke);
        continue;
      }

      const keptRuns: ProjectedStrokePoint[][] = [];
      let currentRun: ProjectedStrokePoint[] = [];
      let pointsChanged = false;
      for (const pt of stroke.points) {
        const id = pt.attachment.objectId;
        const removedPoint = removed?.has(id) ?? false;
        const bumpedVersion = bumped?.get(id);
        const staleTopology =
          bumpedVersion !== undefined &&
          bumpedVersion !== pt.attachment.topologyVersion &&
          pt.corners !== undefined;

        if (removedPoint || staleTopology) {
          layerChanged = true;
          pointsChanged = true;
          if (currentRun.length > 0) {
            keptRuns.push(currentRun);
            currentRun = [];
          }
          continue;
        }

        const mat = baked?.get(id);
        if (mat) {
          _v.set(pt.p[0], pt.p[1], pt.p[2]).applyMatrix4(mat);
          currentRun.push({
            attachment: pt.attachment,
            p: [_v.x, _v.y, _v.z] as [number, number, number],
            pressure: pt.pressure,
            skinIndex: pt.skinIndex,
            skinWeight: pt.skinWeight,
            corners: pt.corners,
            bary: pt.bary,
          });
          layerChanged = true;
          pointsChanged = true;
          continue;
        }

        currentRun.push(pt);
      }

      if (currentRun.length > 0) keptRuns.push(currentRun);
      if (!pointsChanged) {
        nextStrokes.push(stroke);
        continue;
      }

      keptRuns.forEach((points, runIndex) => {
        if (points.length === 0) return;
        nextStrokes.push({
          ...stroke,
          id: runIndex === 0 ? stroke.id : `${stroke.id}_${runIndex}`,
          points,
        });
      });
    }
    if (!layerChanged) return layer;
    anyChange = true;
    return { ...layer, strokes: nextStrokes };
  });

  if (!anyChange) return sketch;
  return { layers: nextLayers };
}

/** Re-export for symmetry — most callers only need the helper. */
export { cloneSceneSketchState };
